import React, { useMemo, useState } from 'react';
import { createFilterOptions } from '@mui/material';

import Autocomplete, { type AutocompleteProps } from 'src/components/input/autocomplete/Autocomplete';
import { lastThirtyDays, useProperties, useShowings } from 'src/services/api';
import type { IShowing } from 'src/api/landlord-showings-api';

type Props = Omit<AutocompleteProps, 'options'> & {
    withShowings?: boolean,
    wholeTeam?: boolean,
    isVacant?: boolean,
    /**
     * Show an option to clear the value.
     */
    isClearable?: boolean,
    /**
     * Text to show in the clearable option.
     * If `isClearable` is `false`, this prop is ignored.
     * @default "None"
     */
    clearableText?: string,
}

export default function SelectProperty({
    value,
    withShowings,
    wholeTeam,
    isVacant,
    isClearable,
    clearableText,
    sx,
    ...props
}: Props) {
    const { data: properties } = useProperties();
    const [startsAfter] = useState(lastThirtyDays);
    const { data: tours } = useShowings({
        starts_after: startsAfter,
        enabled: Boolean(wholeTeam && withShowings),
    });

    const options = useMemo(() => {
        if (!properties) {
            return [];
        }

        let filteredProperties = properties;

        if (withShowings) {
            const propertiesWithAShowing = new Set(tours?.map((showing: IShowing) => {
                return showing.property_id;
            }));

            filteredProperties = filteredProperties.filter((property) => {
                return propertiesWithAShowing.has(property.id);
            });
        }

        if (isVacant) {
            filteredProperties = filteredProperties.filter((property) => {
                return property.is_vacant;
            });
        }

        // Add the selected property to the top of the list if it is not already in the list
        const selectedProperty = properties.find((property) => property.id === Number(value));
        const selectedPropertyNotInOptions = !filteredProperties.find((o) => o.id === selectedProperty?.id);
        if (selectedProperty && selectedPropertyNotInOptions) {
            filteredProperties.unshift(selectedProperty);
        }


        const _options = [...filteredProperties].sort((a, b) => b.id - a.id).map((property) => ({
            value: String(property.id),
            label: property.name,
        }));

        if (isClearable) {
            _options.unshift({ value: '', label: clearableText || 'None' });
        }

        return _options;
    }, [properties, tours, withShowings, isVacant, isClearable, clearableText, value]);

    return (
        <Autocomplete
            {...props}
            value={value}
            options={options}
            filterOptions={filterOptions}
            data-testid="property-select"
            sx={{
                '& .MuiInputBase-input': {
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                },
                ...(sx as object),
            }}
            renderOption={(optionProps, option) => (
                <li {...optionProps} key={option.value}>
                    <span className="ellipsisText" style={{ display: 'block', width: '100%' }}>
                        {option.label}
                    </span>
                </li>
            )}
        />
    );
}

const filterOptions: Props['filterOptions'] = createFilterOptions({
    limit: 100,
});
